// These are like feature flags but are much more limited in their scope.
// The purpose of the functions in this file is to check if we have admin enabled
// a feature for the client. We use this if we are discontinuing a previous feature but
// may need to provide client-by-client access to said feature as it is removed.

import {AuthUser} from "../contexts/auth_types";
import {MinimalProperty, propertyCreatedAt} from "../contexts/properties/logic";
import {getBudgetYearForProperty} from "../contexts/properties/PropertiesContext";

// These values need to match what is in the
// vizibly-backend/src/logic/original-revenue-mkt-rent/OriginalRevenueMarketRentHandler.ts
// file. If you change the location in the backend, YOU MUST UPDATE THIS COMMENT.
export const LAST_ALLOWED_MANUAL_BUDGET_YEAR = 2026;
export const LAST_ALLOWED_ACQUISITION_CREATE_DATE = {year: 2026, month: 6, day: 15};

function isDateAllowed(propertyDate: typeof LAST_ALLOWED_ACQUISITION_CREATE_DATE): boolean {
    return propertyDate.year < LAST_ALLOWED_ACQUISITION_CREATE_DATE.year ||
        (propertyDate.year === LAST_ALLOWED_ACQUISITION_CREATE_DATE.year && propertyDate.month < LAST_ALLOWED_ACQUISITION_CREATE_DATE.month) ||
        (propertyDate.year === LAST_ALLOWED_ACQUISITION_CREATE_DATE.year && propertyDate.month === LAST_ALLOWED_ACQUISITION_CREATE_DATE.month && propertyDate.day < LAST_ALLOWED_ACQUISITION_CREATE_DATE.day)
}

export function isManualMarketRentEnabled(user: AuthUser, property: MinimalProperty, selectedBudgetYear: number): boolean {
    const budgetYear = getBudgetYearForProperty(property, selectedBudgetYear);
    const createdDate = propertyCreatedAt(property);
    if((property.isAcquisitionProperty && isDateAllowed(createdDate)) || (!property.isAcquisitionProperty && budgetYear <= LAST_ALLOWED_MANUAL_BUDGET_YEAR)) {
        return true;
    }

    return user.clientFeatures.allowManualMarketRent;
}

export function isAcquisitionPropertiesEnabled(user: AuthUser | null): boolean {
    return user !== null && !user.clientFeatures.disallowAcquisitionProperties;
}
// by default, all clients get the new Occupancy Manager feature
export function isOldOperationalMetricsUI(user: AuthUser | null): boolean {
    return user?.clientFeatures.useOldOperationalMetricsUI ?? false;
}

// by default, Revenue Wizard should show
export function hideRevenueWizard(user: AuthUser | null): boolean {
    return user?.clientFeatures.hideRevenueWizard ?? false;
}

export function adminModelDownEnabled(user: AuthUser | null): boolean {
    return user?.clientFeatures.adminModelDownEnabled ?? false;
}

export function pushingPayrollDefaultsEnabled(user: AuthUser | null): boolean {
    return user?.clientFeatures.pushingPayrollDefaults ?? false;
}

export function showLossLeaders(user: AuthUser | null): boolean {
    return user?.clientFeatures.showLossLeaders ?? false;
}

export function showWorkflows(user: AuthUser | null): boolean {
    return user?.clientFeatures.showWorkflows ?? false;
}

export function showUserSpecifiedUnitCounts(user: AuthUser | null): boolean {
    return user?.clientFeatures.userSpecifiedUnitCounts ?? false;
}

export function isExecutiveSummaryAccessRestricted(user: AuthUser | null): boolean {
    return user?.clientFeatures.restrictExecutiveSummaryAccess ?? false;
}

export function isInitialGprSetupEnabled(user: AuthUser | null): boolean {
    return user?.clientFeatures.enableInitialGprSetup ?? false;
}

export function showSideBySidePortfolioComparison(user: AuthUser | null): boolean {
    return user?.clientFeatures.sideBySideComparison ?? false;
}

// user arg type is different from the others because of the split between guest and normal users
export function showExecutiveSummaryCustomNarrative(user: Pick<AuthUser, "clientFeatures"> | null): boolean {
    return user?.clientFeatures.executiveSummaryCustomNarrative ?? false;
}
